import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import React, { useState, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import { useParams, useNavigate } from "react-router-dom";
// Data
import MDButton from "../../components/MDButton";
import MDInput from "../../components/MDInput";
import buildQuotationTable from "layouts/quotation/data/quotationTableData";
import QuotationEdit from "./edits/QuotationEdit";
import apis from "../../context/api";
import { timeOf } from "../../utils/format";
import NoAccess from "components/NoAccess";
import { canView, canCreate, canDelete as canDeleteOrder } from "context/permissions";

function Quotation() {
  const { id: urlId } = useParams();
  const navigate = useNavigate();
  // Quotations and Orders share the "Order" module permissions.
  const allowView = canView("Order");
  const allowCreate = canCreate("Order");
  const allowDelete = canDeleteOrder("Order");

  const [quotationUpdate, setQuotationUpdate] = useState(false);
  const [id, setId] = useState(null);
  const [quotationList, setQuotationList] = useState([]);
  const [quotationSearch, setQuotationSearch] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (urlId) {
      setId(urlId);
      setQuotationUpdate(true);
    } else {
      setId(null);
      setQuotationUpdate(false);
    }
  }, [urlId]);

  const handleCancel = () => {
    setQuotationUpdate(false);
    if (urlId) {
      navigate("/quotation");
    }
  };
  const quotationUpdated = () => {
    setQuotationUpdate(false);
    loadTableData();
    if (urlId) {
      navigate("/quotation");
    }
  };
  useEffect(() => {
    loadTableData();
  }, []);
  const editCombo = (id) => {
    navigate(`/quotation/${id}`);
  };
  const convertPIOrder = (id, date, transporterName) => {
    setQuotationUpdate(false);
    apis
      .convertPi(id, date, transporterName)
      .then(() => {
        loadTableData();
      })
      .catch((err) => {
        console.error("Data loading error:", err);
      });
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteId) {
      apis
        .deleteQuotation(deleteId)
        .then(() => {
          setDeleteDialogOpen(false);
          setDeleteId(null);
          loadTableData();
        })
        .catch((error) => {
          console.error("Delete error:", error);
          setDeleteDialogOpen(false);
          setDeleteId(null);
        });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };

  const loadTableData = async () => {
    try {
      setLoading(true);
      apis
        .getAllQuotation()
        .then((data) => {
          setQuotationList(Array.isArray(data) ? data : []);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.error("Can not get Quotations:", err);
        });
    } catch (err) {
      setLoading(false);
      console.error("Data loading error:", err);
    }
  };

  // Newest first; records without updatedAt sort to the bottom.
  const sortByUpdatedDesc = (a, b) => timeOf(b.updatedAt) - timeOf(a.updatedAt);

  const quotationData = useMemo(() => {
    const q = quotationSearch.trim().toLowerCase();
    const filtered = quotationList
      .filter((item) => {
        if (!q) return true;
        const fields = [item.customer?.store_name, item.customer?.name, item.user?.name];
        return fields.some((f) => (f || "").toLowerCase().includes(q));
      })
      .sort(sortByUpdatedDesc);
    return buildQuotationTable(
      filtered,
      (qid) => editCombo(qid),
      (qid, date, transporterName) => convertPIOrder(qid, date, transporterName),
      (qid) => handleDeleteClick(qid),
      allowCreate,
      allowDelete
    );
  }, [quotationList, quotationSearch, allowCreate, allowDelete]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {quotationUpdate && (
        <QuotationEdit onSuccess={quotationUpdated} id={id} onCancel={handleCancel} />
      )}
      {!quotationUpdate && !allowView && (
        <MDBox pt={6} pb={6}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <NoAccess message="You do not have permission to view Quotations." />
            </Grid>
          </Grid>
        </MDBox>
      )}
      {!quotationUpdate && allowView && (
        <MDBox pt={6} pb={6}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                >
                  <MDTypography variant="h6" color="white">
                    Quotation Table
                  </MDTypography>
                </MDBox>
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  px={2}
                  pt={2}
                >
                  <Grid item xs={12} sm={6} md={4}>
                    <MDInput
                      fullWidth
                      label="Search by customer or quoted by"
                      value={quotationSearch}
                      onChange={(e) => setQuotationSearch(e.target.value)}
                    />
                  </Grid>
                  {allowCreate && (
                    <Grid item>
                      <MDButton
                        variant="gradient"
                        color="info"
                        onClick={() => {
                          setId(null);
                          setQuotationUpdate(true);
                        }}
                      >
                        Create
                      </MDButton>
                    </Grid>
                  )}
                </Grid>
                <MDBox pt={3}>
                  {loading ? (
                    <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
                      <CircularProgress />
                    </MDBox>
                  ) : (
                    <DataTable
                      table={{ columns: quotationData.columns, rows: quotationData.rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      )}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Quotation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this quotation? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleDeleteCancel} color="info">
            Cancel
          </MDButton>
          <MDButton onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default Quotation;
